import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import axios from 'axios';
import {
    Button,
    Card,
    Col,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Row,
    Select,
    Space,
    Table,
    Tabs,
    Tag,
    Typography,
    message
} from 'antd';
import {
    CheckOutlined,
    CloseOutlined,
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    PrinterOutlined,
    QrcodeOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import Layout from './Layout';

const { Title, Text } = Typography;

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
});

const locationLabel = row => {
    const w =
        row?.warehouse?.code ||
        row?.warehouse?.name ||
        (row?.warehouse_id ? `Склад #${row.warehouse_id}` : null);
    const c =
        row?.cell?.code ||
        row?.cell?.name ||
        (row?.cell_id ? `Ячейка #${row.cell_id}` : null);
    if (!w && !c) return '—';
    if (w && c) return `${w} / ${c}`;
    return w || c;
};

const userLabel = row => {
    if (!row) return '—';
    if (row.name) return row.name;
    return (
        [row.last_name, row.first_name, row.middle_name]
            .filter(Boolean)
            .join(' ') ||
        row.login ||
        `#${row.id}`
    );
};

const issueLinesKey = lines =>
    JSON.stringify(
        [...(lines || [])]
            .map(line => ({
                i: Number(line.item_id),
                q: Number(line.quantity)
            }))
            .sort((a, b) => a.i - b.i)
    );

const TmcWarehousePage = () => {
    const [items, setItems] = useState([]);
    const [holdings, setHoldings] = useState([]);
    const [pending, setPending] = useState([]);
    const [movements, setMovements] = useState([]);
    const [installers, setInstallers] = useState([]);
    const [issues, setIssues] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState('');

    const [itemModal, setItemModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [opModal, setOpModal] = useState(null);
    const [itemForm] = Form.useForm();
    const [opForm] = Form.useForm();
    const [warehouseForm] = Form.useForm();
    const [cellForm] = Form.useForm();
    const [warehouseModal, setWarehouseModal] = useState(false);
    const [cellModal, setCellModal] = useState(false);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState(null);
    const [labelQr, setLabelQr] = useState(null);
    const [labelLoading, setLabelLoading] = useState(false);
    const [issueLines, setIssueLines] = useState([]);
    const [pickItemId, setPickItemId] = useState(undefined);
    const [pickQty, setPickQty] = useState(1);
    const [issueResult, setIssueResult] = useState(null);
    const [issueQr, setIssueQr] = useState(null);
    const [qrLoading, setQrLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [
                dash,
                itemsRes,
                holdingsRes,
                pendingRes,
                movesRes,
                instRes,
                issuesRes,
                warehousesRes
            ] =
                await Promise.all([
                    axios.get('/api/tmc/dashboard', {
                        headers: authHeaders()
                    }),
                    axios.get('/api/tmc/items', {
                        headers: authHeaders(),
                        params: { q: q || undefined, all: '1' }
                    }),
                    axios.get('/api/tmc/holdings', {
                        headers: authHeaders()
                    }),
                    axios.get('/api/tmc/holdings', {
                        headers: authHeaders(),
                        params: { pending: '1' }
                    }),
                    axios.get('/api/tmc/movements', {
                        headers: authHeaders(),
                        params: { limit: 150 }
                    }),
                    axios.get('/api/tmc/installers', {
                        headers: authHeaders()
                    }),
                    axios.get('/api/tmc/issues', {
                        headers: authHeaders(),
                        params: { status: 'pending' }
                    }),
                    axios.get('/api/tmc/warehouses', {
                        headers: authHeaders()
                    })
                ]);

            setStats(dash.data.data?.stats || {});
            setItems(itemsRes.data.data || []);
            setHoldings(holdingsRes.data.data || []);
            setPending(pendingRes.data.data || []);
            setMovements(movesRes.data.data || []);
            setInstallers(instRes.data.data || []);
            setIssues(issuesRes.data.data || []);
            setWarehouses(warehousesRes.data.data || []);
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    error.message ||
                    'Ошибка загрузки ТМЦ'
            );
        } finally {
            setLoading(false);
        }
    }, [q]);

    useEffect(() => {
        load();
    }, [load]);

    const itemOptions = useMemo(
        () =>
            items
                .filter(i => i.is_active)
                .map(i => ({
                    value: i.id,
                    label: `${i.sku ? `${i.sku} · ` : ''}${i.name} (склад: ${i.warehouse_qty} ${i.unit})`,
                    item: i
                })),
        [items]
    );

    const issueItemOptions = useMemo(
        () =>
            items
                .filter(i => i.is_active)
                .map(i => {
                    const stock = Number(i.warehouse_qty) || 0;
                    const inCart =
                        Number(
                            issueLines.find(l => l.item_id === i.id)
                                ?.quantity
                        ) || 0;
                    const left = Math.max(0, stock - inCart);
                    return {
                        value: i.id,
                        disabled: stock <= 0 || left <= 0,
                        label:
                            stock <= 0
                                ? `${i.sku ? `${i.sku} · ` : ''}${i.name} (нет на складе)`
                                : `${i.sku ? `${i.sku} · ` : ''}${i.name} (склад: ${stock} ${i.unit}${
                                      inCart
                                          ? `, в выдаче ${inCart}`
                                          : ''
                                  })`
                    };
                }),
        [items, issueLines]
    );

    const pickedIssueItem = useMemo(
        () => items.find(i => i.id === pickItemId) || null,
        [items, pickItemId]
    );

    const pickedStockLeft = useMemo(() => {
        if (!pickedIssueItem) return 0;
        const stock = Number(pickedIssueItem.warehouse_qty) || 0;
        const inCart =
            Number(
                issueLines.find(l => l.item_id === pickedIssueItem.id)
                    ?.quantity
            ) || 0;
        return Math.round((stock - inCart) * 1000) / 1000;
    }, [pickedIssueItem, issueLines]);

    const issueLinesValid = useMemo(() => {
        if (!issueLines.length) return false;
        return issueLines.every(line => {
            const item = items.find(i => i.id === line.item_id);
            const stock = Number(item?.warehouse_qty) || 0;
            const qty = Number(line.quantity);
            return item && qty > 0 && qty <= stock;
        });
    }, [issueLines, items]);

    const warehouseOptions = useMemo(
        () =>
            warehouses
                .filter(w => w.is_active !== false)
                .map(w => ({
                    value: w.id,
                    label: w.code ? `${w.code} · ${w.name}` : w.name
                })),
        [warehouses]
    );

    const cellsOfSelectedWarehouse = useMemo(() => {
        const wh = warehouses.find(
            w => Number(w.id) === Number(selectedWarehouseId)
        );
        return wh?.cells || [];
    }, [warehouses, selectedWarehouseId]);

    const cellOptions = useMemo(
        () =>
            cellsOfSelectedWarehouse
                .filter(c => c.is_active !== false)
                .map(c => ({
                    value: c.id,
                    label: c.code ? `${c.code} · ${c.name}` : c.name
                })),
        [cellsOfSelectedWarehouse]
    );

    const openCreateItem = () => {
        setEditingItem(null);
        itemForm.resetFields();
        itemForm.setFieldsValue({ unit: 'шт', min_qty: 0 });
        setSelectedWarehouseId(null);
        setItemModal(true);
    };

    const openEditItem = row => {
        setEditingItem(row);
        setSelectedWarehouseId(row.warehouse_id || null);
        itemForm.setFieldsValue({
            name: row.name,
            sku: row.sku,
            unit: row.unit,
            min_qty: row.min_qty,
            description: row.description,
            warehouse_id: row.warehouse_id || undefined,
            cell_id: row.cell_id || undefined,
            is_active: row.is_active !== false
        });
        setItemModal(true);
    };

    const saveWarehouse = async values => {
        try {
            await axios.post('/api/tmc/warehouses', values, {
                headers: authHeaders()
            });
            message.success('Склад создан');
            setWarehouseModal(false);
            warehouseForm.resetFields();
            load();
        } catch (error) {
            message.error(
                error.response?.data?.message || error.message
            );
        }
    };

    const saveCell = async values => {
        try {
            await axios.post('/api/tmc/cells', values, {
                headers: authHeaders()
            });
            message.success('Ячейка создана');
            setCellModal(false);
            cellForm.resetFields();
            load();
        } catch (error) {
            message.error(
                error.response?.data?.message || error.message
            );
        }
    };

    const openItemLabelQr = async row => {
        setLabelLoading(true);
        try {
            const { data } = await axios.post(
                `/api/tmc/items/${row.id}/qr`,
                {},
                { headers: authHeaders() }
            );
            setLabelQr(data.data);
        } catch (error) {
            message.error(
                error.response?.data?.message || error.message
            );
        } finally {
            setLabelLoading(false);
        }
    };

    const printItemLabel = () => {
        if (!labelQr?.qr_data_url) return;
        const w = window.open('', '_blank', 'width=420,height=560');
        if (!w) {
            message.error('Разрешите всплывающие окна для печати');
            return;
        }
        const title = [
            labelQr.name,
            labelQr.sku ? `(${labelQr.sku})` : ''
        ]
            .filter(Boolean)
            .join(' ');
        w.document.write(`<!doctype html><html><head><title>${title}</title>
<style>
body{font-family:Arial,sans-serif;text-align:center;padding:24px;color:#111}
img{width:240px;height:240px}
h1{font-size:18px;margin:12px 0 4px}
.loc{font-size:16px;font-weight:700;margin:8px 0}
.meta{font-size:12px;color:#555}
@media print{body{padding:0}}
</style></head><body>
<img src="${labelQr.qr_data_url}" alt="QR"/>
<h1>${title}</h1>
<div class="loc">${labelQr.label || ''}</div>
<div class="meta">Склад #${labelQr.warehouse?.id || '—'} · Ячейка #${labelQr.cell?.id || '—'} · ID ${labelQr.item_id}</div>
<script>window.onload=function(){window.print();}</script>
</body></html>`);
        w.document.close();
    };

    const addIssueLine = () => {
        const item = pickedIssueItem;
        const qty = Number(pickQty);
        if (!item) {
            message.warning('Выберите позицию');
            return;
        }
        const stock = Number(item.warehouse_qty) || 0;
        if (stock <= 0) {
            message.error(`Нет остатка на складе: ${item.name}`);
            return;
        }
        if (!Number.isFinite(qty) || qty <= 0) {
            message.warning('Количество должно быть больше 0');
            return;
        }
        const already = issueLines.find(l => l.item_id === item.id);
        const nextQty =
            Math.round(
                ((already ? Number(already.quantity) : 0) + qty) * 1000
            ) / 1000;
        if (nextQty > stock) {
            message.error(
                `Нельзя выдать больше остатка: ${item.name} (склад ${stock} ${item.unit})`
            );
            return;
        }
        if (already) {
            setIssueLines(list =>
                list.map(line =>
                    line.item_id === item.id
                        ? { ...line, quantity: nextQty, max: stock }
                        : line
                )
            );
        } else {
            setIssueLines(list => [
                ...list,
                {
                    item_id: item.id,
                    name: item.name,
                    sku: item.sku,
                    unit: item.unit,
                    quantity: qty,
                    max: stock
                }
            ]);
        }
        setPickQty(1);
    };

    const setIssueLineQty = (itemId, value) => {
        const item = items.find(i => i.id === itemId);
        const stock = Number(item?.warehouse_qty) || 0;
        const raw = Number(value);
        if (!Number.isFinite(raw) || raw <= 0) {
            message.warning('Количество должно быть больше 0');
            return;
        }
        const qty = Math.round(raw * 1000) / 1000;
        if (qty > stock) {
            message.error(
                `На складе только ${stock} ${item?.unit || ''}`.trim()
            );
            setIssueLines(list =>
                list.map(line =>
                    line.item_id === itemId
                        ? { ...line, quantity: stock, max: stock }
                        : line
                )
            );
            return;
        }
        setIssueLines(list =>
            list.map(line =>
                line.item_id === itemId
                    ? { ...line, quantity: qty, max: stock }
                    : line
            )
        );
    };

    const saveItem = async values => {
        try {
            const payload = {
                ...values,
                warehouse_id: values.warehouse_id || null,
                cell_id: values.cell_id || null
            };
            if (editingItem) {
                await axios.put(
                    `/api/tmc/items/${editingItem.id}`,
                    payload,
                    { headers: authHeaders() }
                );
                message.success('Позиция обновлена');
            } else {
                await axios.post('/api/tmc/items', payload, {
                    headers: authHeaders()
                });
                message.success('Позиция создана');
            }
            setItemModal(false);
            setEditingItem(null);
            itemForm.resetFields();
            load();
        } catch (error) {
            message.error(
                error.response?.data?.message || error.message
            );
        }
    };

    const runOp = async values => {
        try {
            if (opModal === 'receive') {
                await axios.post(
                    '/api/tmc/receive',
                    {
                        item_id: values.item_id,
                        quantity: values.quantity,
                        note: values.note
                    },
                    { headers: authHeaders() }
                );
                message.success('Приём на склад выполнен');
            } else if (opModal === 'issue') {
                if (!issueLines.length) {
                    message.warning('Добавьте позиции в выдачу');
                    return;
                }
                const bad = issueLines.find(line => {
                    const item = items.find(i => i.id === line.item_id);
                    const stock = Number(item?.warehouse_qty) || 0;
                    const qty = Number(line.quantity);
                    return !item || !(qty > 0) || qty > stock;
                });
                if (bad) {
                    const item = items.find(i => i.id === bad.item_id);
                    const stock = Number(item?.warehouse_qty) || 0;
                    message.error(
                        Number(bad.quantity) <= 0
                            ? 'Нельзя выдать нулевое количество'
                            : `Нельзя выдать больше остатка: ${
                                  item?.name || 'позиция'
                              } (склад ${stock})`
                    );
                    return;
                }
                const { data } = await axios.post(
                    '/api/tmc/issue',
                    {
                        user_id: values.user_id,
                        note: values.note,
                        code: issueQr?.code || undefined,
                        lines: issueLines.map(line => ({
                            item_id: line.item_id,
                            quantity: line.quantity
                        }))
                    },
                    { headers: authHeaders() }
                );
                setIssueResult(data.data || data);
                setIssueLines([]);
                setIssueQr(null);
                message.success(
                    'Выдача оформлена и ждёт подтверждения сотрудника'
                );
            } else if (opModal === 'writeoff') {
                await axios.post(
                    '/api/tmc/writeoff',
                    {
                        item_id: values.item_id,
                        quantity: values.quantity,
                        note: values.note
                    },
                    { headers: authHeaders() }
                );
                message.success('Списание со склада выполнено');
            }
            setOpModal(null);
            opForm.resetFields();
            setPickItemId(undefined);
            setPickQty(1);
            load();
        } catch (error) {
            message.error(
                error.response?.data?.message || error.message
            );
        }
    };

    const issueUserId = Form.useWatch('user_id', opForm);
    const qrBusyRef = useRef(false);

    const generateIssueQr = useCallback(
        async ({ silent = false } = {}) => {
            const userId = opForm.getFieldValue('user_id');
            if (!userId) {
                if (!silent) {
                    message.warning('Сначала выберите сотрудника');
                }
                return;
            }
            if (!issueLines.length) {
                if (!silent) {
                    message.warning(
                        'Сначала добавьте позиции в выдачу'
                    );
                }
                return;
            }
            if (qrBusyRef.current) return;
            qrBusyRef.current = true;
            setQrLoading(true);
            try {
                const { data } = await axios.post(
                    '/api/tmc/issues/qr',
                    {
                        user_id: userId,
                        note: opForm.getFieldValue('note'),
                        code: issueQr?.code || undefined,
                        lines: issueLines.map(line => ({
                            item_id: line.item_id,
                            quantity: line.quantity
                        }))
                    },
                    { headers: authHeaders() }
                );
                const next = data.data || data;
                setIssueQr({
                    ...next,
                    lines_key: `${userId}:${issueLinesKey(issueLines)}`
                });
                if (!silent) {
                    message.success('QR-код сохранён в базе');
                }
            } catch (error) {
                if (!silent) {
                    message.error(
                        error.response?.data?.message ||
                            error.message
                    );
                }
            } finally {
                qrBusyRef.current = false;
                setQrLoading(false);
            }
        },
        [issueLines, issueQr?.code, opForm]
    );

    useEffect(() => {
        if (opModal !== 'issue') return;
        if (!issueQr?.code) return;
        if (!issueLines.length) {
            setIssueQr(null);
            return;
        }
        const key = `${issueUserId || ''}:${issueLinesKey(issueLines)}`;
        if (key === issueQr.lines_key) return;
        const timer = setTimeout(() => {
            generateIssueQr({ silent: true });
        }, 400);
        return () => clearTimeout(timer);
    }, [
        issueLines,
        issueUserId,
        issueQr?.code,
        issueQr?.lines_key,
        opModal,
        generateIssueQr
    ]);

    const cancelIssue = async id => {
        try {
            await axios.post(
                `/api/tmc/issues/${id}/cancel`,
                {},
                { headers: authHeaders() }
            );
            message.success('Выдача отменена, товар вернулся на склад');
            load();
        } catch (error) {
            message.error(
                error.response?.data?.message || error.message
            );
        }
    };

    const showIssueQr = async id => {
        try {
            const { data } = await axios.get(`/api/tmc/issues/${id}`, {
                headers: authHeaders()
            });
            setIssueResult(data.data || data);
        } catch (error) {
            message.error(
                error.response?.data?.message || error.message
            );
        }
    };

    const confirmReturn = async id => {
        try {
            await axios.post(
                `/api/tmc/returns/${id}/confirm`,
                {},
                { headers: authHeaders() }
            );
            message.success('Возврат принят на склад');
            load();
        } catch (error) {
            message.error(
                error.response?.data?.message || error.message
            );
        }
    };

    const rejectReturn = async id => {
        try {
            await axios.post(
                `/api/tmc/returns/${id}/reject`,
                {},
                { headers: authHeaders() }
            );
            message.success('Возврат отклонён, остаётся у монтажника');
            load();
        } catch (error) {
            message.error(
                error.response?.data?.message || error.message
            );
        }
    };

    const itemColumns = [
        { title: 'ID', dataIndex: 'id', width: 70 },
        { title: 'Название', dataIndex: 'name' },
        { title: 'Артикул', dataIndex: 'sku', width: 120 },
        { title: 'Ед.', dataIndex: 'unit', width: 70 },
        {
            title: 'Склад',
            dataIndex: 'warehouse_qty',
            width: 100,
            render: (v, row) => (
                <Text
                    type={
                        Number(v) <= Number(row.min_qty)
                            ? 'danger'
                            : undefined
                    }
                    strong
                >
                    {v}
                </Text>
            )
        },
        {
            title: 'У монтажников',
            dataIndex: 'issued_qty',
            width: 120
        },
        {
            title: 'На объектах',
            dataIndex: 'on_object_qty',
            width: 110,
            render: v =>
                Number(v) > 0 ? (
                    <Tag color="green">{v}</Tag>
                ) : (
                    '—'
                )
        },
        {
            title: 'Ожидает выдачи',
            dataIndex: 'issue_pending_qty',
            width: 130,
            render: v =>
                Number(v) > 0 ? (
                    <Tag color="purple">{v}</Tag>
                ) : (
                    '—'
                )
        },
        {
            title: 'Ожидает возврата',
            dataIndex: 'return_pending_qty',
            width: 140,
            render: v =>
                Number(v) > 0 ? (
                    <Tag color="orange">{v}</Tag>
                ) : (
                    '—'
                )
        },
        {
            title: 'Статус',
            dataIndex: 'is_active',
            width: 100,
            render: v =>
                v ? (
                    <Tag color="green">активна</Tag>
                ) : (
                    <Tag>выкл</Tag>
                )
        },
        {
            title: 'Место',
            width: 160,
            render: (_, row) => locationLabel(row)
        },
        {
            title: '',
            width: 120,
            fixed: 'right',
            render: (_, row) => (
                <Space size={4}>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openEditItem(row)}
                    />
                    <Button
                        size="small"
                        icon={<QrcodeOutlined />}
                        loading={labelLoading}
                        disabled={!row.warehouse_id || !row.cell_id}
                        title={
                            row.warehouse_id && row.cell_id
                                ? 'QR-метка места хранения'
                                : 'Сначала укажите склад и ячейку'
                        }
                        onClick={() => openItemLabelQr(row)}
                    />
                </Space>
            )
        }
    ];

    const holdingColumns = [
        {
            title: 'ТМЦ',
            render: (_, row) => row.item?.name || '—'
        },
        {
            title: 'Монтажник',
            render: (_, row) => userLabel(row.user)
        },
        { title: 'Кол-во', dataIndex: 'quantity', width: 90 },
        {
            title: 'Статус',
            dataIndex: 'status',
            width: 160,
            render: (v, row) => {
                if (v === 'issue_pending') {
                    return (
                        <Tag color="purple">
                            ждёт подтверждения
                        </Tag>
                    );
                }
                if (v === 'return_pending') {
                    return (
                        <Tag color="orange">
                            ожидает приёмки
                        </Tag>
                    );
                }
                if (v === 'on_object') {
                    return (
                        <Tag color="green">
                            на объекте
                            {row.task?.external_id
                                ? ` · ${row.task.external_id}`
                                : ''}
                        </Tag>
                    );
                }
                if (v === 'transfer_pending') {
                    return (
                        <Tag color="purple">передача коллеге</Tag>
                    );
                }
                return <Tag color="blue">числится</Tag>;
            }
        },
        {
            title: 'Заявка',
            width: 120,
            render: (_, row) =>
                row.task?.external_id ||
                (row.task_id ? `#${row.task_id}` : '—')
        },
        {
            title: 'Выдано',
            dataIndex: 'issued_at',
            width: 160,
            render: v =>
                v ? new Date(v).toLocaleString('ru-RU') : '—'
        }
    ];

    const pendingColumns = [
        ...holdingColumns.filter(c => c.dataIndex !== 'status'),
        {
            title: 'Запрошен',
            dataIndex: 'return_requested_at',
            width: 160,
            render: v =>
                v ? new Date(v).toLocaleString('ru-RU') : '—'
        },
        {
            title: 'Действия',
            width: 220,
            render: (_, row) => (
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<CheckOutlined />}
                        onClick={() => confirmReturn(row.id)}
                    >
                        Принять
                    </Button>
                    <Button
                        danger
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => rejectReturn(row.id)}
                    >
                        Отклонить
                    </Button>
                </Space>
            )
        }
    ];

    const moveColumns = [
        {
            title: 'Время',
            dataIndex: 'created_at',
            width: 160,
            render: v =>
                v ? new Date(v).toLocaleString('ru-RU') : '—'
        },
        {
            title: 'Тип',
            dataIndex: 'type',
            width: 130,
            render: v => {
                const map = {
                    receive: ['приём', 'green'],
                    issue: ['выдача', 'blue'],
                    issue_accept: ['получено', 'green'],
                    writeoff: ['списание', 'red'],
                    issue_cancel: ['отмена выдачи', 'default'],
                    return_request: ['запрос возврата', 'orange'],
                    return_confirm: ['возврат принят', 'green'],
                    return_reject: ['возврат отклонён', 'default'],
                    peer_offer: ['QR-передача', 'purple'],
                    peer_transfer_out: ['передал коллеге', 'blue'],
                    peer_transfer_in: ['получил от коллеги', 'green'],
                    install_on_object: ['на объект', 'green'],
                    dismantle_from_object: ['демонтаж с объекта', 'orange']
                };
                const [text, color] = map[v] || [v, 'default'];
                return <Tag color={color}>{text}</Tag>;
            }
        },
        {
            title: 'ТМЦ',
            render: (_, row) => row.item?.name || '—'
        },
        { title: 'Кол-во', dataIndex: 'quantity', width: 90 },
        {
            title: 'Монтажник',
            render: (_, row) => userLabel(row.user)
        },
        {
            title: 'Кто',
            render: (_, row) => userLabel(row.creator)
        },
        {
            title: 'Примечание',
            dataIndex: 'note',
            ellipsis: true
        }
    ];

    return (
        <Layout>
            <div style={{ padding: 24 }}>
                <Space
                    style={{
                        width: '100%',
                        justifyContent: 'space-between',
                        marginBottom: 16
                    }}
                    wrap
                >
                    <div>
                        <Title level={3} style={{ margin: 0 }}>
                            ТМЦ / Склад
                        </Title>
                        <Text type="secondary">
                            Остатки, приём, выдача, списание и
                            подтверждение возвратов
                        </Text>
                    </div>
                    <Space wrap>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={load}
                        >
                            Обновить
                        </Button>
                        <Button
                            icon={<PlusOutlined />}
                            onClick={openCreateItem}
                        >
                            Позиция
                        </Button>
                        <Button onClick={() => setOpModal('receive')}>
                            Приём
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => {
                                setIssueLines([]);
                                setPickItemId(undefined);
                                setPickQty(1);
                                setIssueQr(null);
                                opForm.resetFields();
                                setOpModal('issue');
                            }}
                        >
                            Выдача
                        </Button>
                        <Button
                            danger
                            onClick={() => setOpModal('writeoff')}
                        >
                            Списание
                        </Button>
                    </Space>
                </Space>

                <Row gutter={16} style={{ marginBottom: 16 }}>
                    {[
                        ['Позиций', stats.items],
                        ['С остатком', stats.warehouseSku],
                        ['Ниже минимума', stats.lowStock],
                        [
                            'Ждут подтверждения',
                            stats.pendingIssues
                        ],
                        ['Ожидают возврата', stats.pendingReturns]
                    ].map(([label, value]) => (
                        <Col xs={12} md={4} lg={4} key={label}>
                            <Card size="small">
                                <Text type="secondary">{label}</Text>
                                <Title level={3} style={{ margin: 0 }}>
                                    {value || 0}
                                </Title>
                            </Card>
                        </Col>
                    ))}
                </Row>

                <Tabs
                    items={[
                        {
                            key: 'stock',
                            label: 'Остатки',
                            children: (
                                <Card size="small">
                                    <Input.Search
                                        placeholder="Поиск по названию / артикулу"
                                        allowClear
                                        style={{
                                            maxWidth: 360,
                                            marginBottom: 12
                                        }}
                                        onSearch={setQ}
                                    />
                                    <Table
                                        size="small"
                                        rowKey="id"
                                        loading={loading}
                                        columns={itemColumns}
                                        dataSource={items}
                                        scroll={{ x: 1100 }}
                                        pagination={{
                                            pageSize: 15
                                        }}
                                    />
                                </Card>
                            )
                        },
                        {
                            key: 'warehouses',
                            label: 'Склады и ячейки',
                            children: (
                                <Card
                                    size="small"
                                    extra={
                                        <Space wrap>
                                            <Button
                                                icon={<PlusOutlined />}
                                                type="primary"
                                                onClick={() => {
                                                    warehouseForm.resetFields();
                                                    setWarehouseModal(true);
                                                }}
                                            >
                                                Склад
                                            </Button>
                                            <Button
                                                icon={<PlusOutlined />}
                                                disabled={!warehouses.length}
                                                onClick={() => {
                                                    cellForm.resetFields();
                                                    if (warehouses[0]) {
                                                        cellForm.setFieldsValue({
                                                            warehouse_id:
                                                                warehouses[0].id
                                                        });
                                                    }
                                                    setCellModal(true);
                                                }}
                                            >
                                                Ячейка
                                            </Button>
                                        </Space>
                                    }
                                >
                                    {!warehouses.length ? (
                                        <Text type="secondary">
                                            Создайте склад, затем добавьте
                                            ячейки. После этого назначьте
                                            номенклатуре место хранения и
                                            распечатайте QR-метку.
                                        </Text>
                                    ) : (
                                        <Space
                                            direction="vertical"
                                            style={{ width: '100%' }}
                                            size={16}
                                        >
                                            {warehouses.map(wh => (
                                                <Card
                                                    key={wh.id}
                                                    size="small"
                                                    type="inner"
                                                    title={
                                                        <Space wrap>
                                                            <Text strong>
                                                                {wh.name}
                                                            </Text>
                                                            {wh.code ? (
                                                                <Tag>
                                                                    {wh.code}
                                                                </Tag>
                                                            ) : null}
                                                            <Text type="secondary">
                                                                #{wh.id}
                                                            </Text>
                                                            {!wh.is_active ? (
                                                                <Tag>выкл</Tag>
                                                            ) : null}
                                                        </Space>
                                                    }
                                                    extra={
                                                        <Button
                                                            size="small"
                                                            icon={
                                                                <PlusOutlined />
                                                            }
                                                            onClick={() => {
                                                                cellForm.resetFields();
                                                                cellForm.setFieldsValue(
                                                                    {
                                                                        warehouse_id:
                                                                            wh.id
                                                                    }
                                                                );
                                                                setCellModal(
                                                                    true
                                                                );
                                                            }}
                                                        >
                                                            Ячейка
                                                        </Button>
                                                    }
                                                >
                                                    {wh.address ? (
                                                        <Text
                                                            type="secondary"
                                                            style={{
                                                                display:
                                                                    'block',
                                                                marginBottom: 8
                                                            }}
                                                        >
                                                            {wh.address}
                                                        </Text>
                                                    ) : null}
                                                    <Table
                                                        size="small"
                                                        rowKey="id"
                                                        pagination={false}
                                                        locale={{
                                                            emptyText:
                                                                'Нет ячеек'
                                                        }}
                                                        dataSource={
                                                            wh.cells || []
                                                        }
                                                        columns={[
                                                            {
                                                                title: 'ID',
                                                                dataIndex: 'id',
                                                                width: 70
                                                            },
                                                            {
                                                                title: 'Код',
                                                                dataIndex:
                                                                    'code',
                                                                width: 100,
                                                                render: v =>
                                                                    v || '—'
                                                            },
                                                            {
                                                                title: 'Название',
                                                                dataIndex:
                                                                    'name'
                                                            },
                                                            {
                                                                title: 'Статус',
                                                                dataIndex:
                                                                    'is_active',
                                                                width: 90,
                                                                render: v =>
                                                                    v ? (
                                                                        <Tag color="green">
                                                                            акт.
                                                                        </Tag>
                                                                    ) : (
                                                                        <Tag>
                                                                            выкл
                                                                        </Tag>
                                                                    )
                                                            },
                                                            {
                                                                title: 'Позиций',
                                                                width: 90,
                                                                render: (
                                                                    _,
                                                                    cell
                                                                ) =>
                                                                    items.filter(
                                                                        i =>
                                                                            Number(
                                                                                i.cell_id
                                                                            ) ===
                                                                            Number(
                                                                                cell.id
                                                                            )
                                                                    ).length
                                                            }
                                                        ]}
                                                    />
                                                </Card>
                                            ))}
                                        </Space>
                                    )}
                                </Card>
                            )
                        },
                        {
                            key: 'issues',
                            label: (
                                <span>
                                    Выдачи{' '}
                                    {issues.length > 0 && (
                                        <Tag color="purple">
                                            {issues.length}
                                        </Tag>
                                    )}
                                </span>
                            ),
                            children: (
                                <Card size="small">
                                    <Table
                                        size="small"
                                        rowKey="id"
                                        loading={loading}
                                        dataSource={issues}
                                        pagination={{ pageSize: 10 }}
                                        locale={{
                                            emptyText:
                                                'Нет выдач, ожидающих подтверждения'
                                        }}
                                        expandable={{
                                            expandedRowRender: row => (
                                                <Table
                                                    size="small"
                                                    pagination={false}
                                                    rowKey="holding_id"
                                                    dataSource={
                                                        row.lines || []
                                                    }
                                                    columns={[
                                                        {
                                                            title: 'Позиция',
                                                            dataIndex:
                                                                'name'
                                                        },
                                                        {
                                                            title: 'Артикул',
                                                            dataIndex:
                                                                'sku',
                                                            width: 120
                                                        },
                                                        {
                                                            title: 'Кол-во',
                                                            render: (
                                                                _,
                                                                line
                                                            ) =>
                                                                `${line.quantity} ${line.unit}`
                                                        }
                                                    ]}
                                                />
                                            )
                                        }}
                                        columns={[
                                            {
                                                title: 'Код',
                                                dataIndex: 'code',
                                                width: 120,
                                                render: v => (
                                                    <Text code>
                                                        {v}
                                                    </Text>
                                                )
                                            },
                                            {
                                                title: 'Сотрудник',
                                                render: (_, row) =>
                                                    userLabel(row.user)
                                            },
                                            {
                                                title: 'Позиций',
                                                width: 90,
                                                render: (_, row) =>
                                                    (
                                                        row.lines || []
                                                    ).length
                                            },
                                            {
                                                title: 'Создана',
                                                dataIndex: 'created_at',
                                                width: 170,
                                                render: v =>
                                                    v
                                                        ? new Date(
                                                              v
                                                          ).toLocaleString(
                                                              'ru-RU'
                                                          )
                                                        : '—'
                                            },
                                            {
                                                title: 'Статус',
                                                width: 160,
                                                render: () => (
                                                    <Tag color="purple">
                                                        ждёт
                                                        подтверждения
                                                    </Tag>
                                                )
                                            },
                                            {
                                                title: 'Действия',
                                                width: 220,
                                                render: (_, row) => (
                                                    <Space>
                                                        <Button
                                                            size="small"
                                                            icon={
                                                                <QrcodeOutlined />
                                                            }
                                                            onClick={() =>
                                                                showIssueQr(
                                                                    row.id
                                                                )
                                                            }
                                                        >
                                                            QR
                                                        </Button>
                                                        <Popconfirm
                                                            title="Вернуть товар на склад?"
                                                            okText="Отменить выдачу"
                                                            cancelText="Нет"
                                                            onConfirm={() =>
                                                                cancelIssue(
                                                                    row.id
                                                                )
                                                            }
                                                        >
                                                            <Button
                                                                danger
                                                                size="small"
                                                            >
                                                                Отменить
                                                            </Button>
                                                        </Popconfirm>
                                                    </Space>
                                                )
                                            }
                                        ]}
                                    />
                                </Card>
                            )
                        },
                        {
                            key: 'pending',
                            label: (
                                <span>
                                    Возвраты{' '}
                                    {pending.length > 0 && (
                                        <Tag color="orange">
                                            {pending.length}
                                        </Tag>
                                    )}
                                </span>
                            ),
                            children: (
                                <Card size="small">
                                    <Table
                                        size="small"
                                        rowKey="id"
                                        loading={loading}
                                        columns={pendingColumns}
                                        dataSource={pending}
                                        pagination={{
                                            pageSize: 15
                                        }}
                                        locale={{
                                            emptyText:
                                                'Нет ожидающих возвратов'
                                        }}
                                    />
                                </Card>
                            )
                        },
                        {
                            key: 'holdings',
                            label: 'У монтажников',
                            children: (
                                <Card size="small">
                                    <Table
                                        size="small"
                                        rowKey="id"
                                        loading={loading}
                                        columns={holdingColumns}
                                        dataSource={holdings}
                                        pagination={{
                                            pageSize: 15
                                        }}
                                    />
                                </Card>
                            )
                        },
                        {
                            key: 'log',
                            label: 'Журнал',
                            children: (
                                <Card size="small">
                                    <Table
                                        size="small"
                                        rowKey="id"
                                        loading={loading}
                                        columns={moveColumns}
                                        dataSource={movements}
                                        pagination={{
                                            pageSize: 20
                                        }}
                                    />
                                </Card>
                            )
                        }
                    ]}
                />

                <Modal
                    title={
                        editingItem
                            ? 'Редактирование позиции ТМЦ'
                            : 'Новая позиция ТМЦ'
                    }
                    open={itemModal}
                    onCancel={() => {
                        setItemModal(false);
                        setEditingItem(null);
                    }}
                    onOk={() => itemForm.submit()}
                    destroyOnClose
                >
                    <Form
                        form={itemForm}
                        layout="vertical"
                        onFinish={saveItem}
                        initialValues={{ unit: 'шт', min_qty: 0 }}
                    >
                        <Form.Item
                            name="name"
                            label="Название"
                            rules={[{ required: true }]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item name="sku" label="Артикул">
                            <Input />
                        </Form.Item>
                        <Form.Item name="unit" label="Ед. изм.">
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="min_qty"
                            label="Минимальный остаток"
                        >
                            <InputNumber
                                min={0}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="warehouse_id"
                            label="Склад"
                        >
                            <Select
                                allowClear
                                placeholder="Выберите склад"
                                options={warehouseOptions}
                                onChange={value => {
                                    setSelectedWarehouseId(
                                        value || null
                                    );
                                    itemForm.setFieldsValue({
                                        cell_id: undefined
                                    });
                                }}
                            />
                        </Form.Item>
                        <Form.Item name="cell_id" label="Ячейка">
                            <Select
                                allowClear
                                placeholder={
                                    selectedWarehouseId
                                        ? 'Выберите ячейку'
                                        : 'Сначала выберите склад'
                                }
                                disabled={!selectedWarehouseId}
                                options={cellOptions}
                            />
                        </Form.Item>
                        <Form.Item
                            name="description"
                            label="Описание"
                        >
                            <Input.TextArea rows={2} />
                        </Form.Item>
                    </Form>
                </Modal>

                <Modal
                    title="Новый склад"
                    open={warehouseModal}
                    onCancel={() => setWarehouseModal(false)}
                    onOk={() => warehouseForm.submit()}
                    destroyOnClose
                >
                    <Form
                        form={warehouseForm}
                        layout="vertical"
                        onFinish={saveWarehouse}
                    >
                        <Form.Item
                            name="name"
                            label="Название"
                            rules={[{ required: true }]}
                        >
                            <Input placeholder="Основной склад" />
                        </Form.Item>
                        <Form.Item name="code" label="Код">
                            <Input placeholder="WH-1" />
                        </Form.Item>
                        <Form.Item name="address" label="Адрес">
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="description"
                            label="Описание"
                        >
                            <Input.TextArea rows={2} />
                        </Form.Item>
                    </Form>
                </Modal>

                <Modal
                    title="Новая ячейка"
                    open={cellModal}
                    onCancel={() => setCellModal(false)}
                    onOk={() => cellForm.submit()}
                    destroyOnClose
                >
                    <Form
                        form={cellForm}
                        layout="vertical"
                        onFinish={saveCell}
                    >
                        <Form.Item
                            name="warehouse_id"
                            label="Склад"
                            rules={[{ required: true }]}
                        >
                            <Select
                                options={warehouseOptions}
                                placeholder="Выберите склад"
                            />
                        </Form.Item>
                        <Form.Item
                            name="name"
                            label="Название"
                            rules={[{ required: true }]}
                        >
                            <Input placeholder="Ячейка 1" />
                        </Form.Item>
                        <Form.Item name="code" label="Код">
                            <Input placeholder="1 или A-01" />
                        </Form.Item>
                        <Form.Item
                            name="description"
                            label="Описание"
                        >
                            <Input.TextArea rows={2} />
                        </Form.Item>
                    </Form>
                </Modal>

                <Modal
                    title="QR-метка номенклатуры"
                    open={!!labelQr}
                    onCancel={() => setLabelQr(null)}
                    footer={[
                        <Button
                            key="close"
                            onClick={() => setLabelQr(null)}
                        >
                            Закрыть
                        </Button>,
                        <Button
                            key="print"
                            type="primary"
                            icon={<PrinterOutlined />}
                            onClick={printItemLabel}
                        >
                            Печать
                        </Button>
                    ]}
                >
                    {labelQr ? (
                        <div style={{ textAlign: 'center' }}>
                            {labelQr.qr_data_url ? (
                                <img
                                    src={labelQr.qr_data_url}
                                    alt="QR метка"
                                    style={{
                                        width: 240,
                                        height: 240
                                    }}
                                />
                            ) : (
                                <Text type="danger">
                                    Не удалось сформировать QR
                                </Text>
                            )}
                            <Title level={5} style={{ marginTop: 12 }}>
                                {labelQr.name}
                                {labelQr.sku
                                    ? ` · ${labelQr.sku}`
                                    : ''}
                            </Title>
                            <Text strong>{labelQr.label}</Text>
                            <div style={{ marginTop: 8 }}>
                                <Text type="secondary">
                                    Склад #{labelQr.warehouse?.id} ·
                                    Ячейка #{labelQr.cell?.id}
                                </Text>
                            </div>
                            <Text
                                type="secondary"
                                style={{
                                    display: 'block',
                                    marginTop: 12,
                                    fontSize: 12
                                }}
                            >
                                Распечатайте и наклейте на место
                                хранения / упаковку
                            </Text>
                        </div>
                    ) : null}
                </Modal>

                <Modal
                    title={
                        opModal === 'receive'
                            ? 'Приём на склад'
                            : 'Списание со склада'
                    }
                    open={opModal === 'receive' || opModal === 'writeoff'}
                    onCancel={() => {
                        setOpModal(null);
                        opForm.resetFields();
                    }}
                    onOk={() => opForm.submit()}
                    destroyOnClose
                >
                    <Form
                        form={opForm}
                        layout="vertical"
                        onFinish={runOp}
                    >
                        <Form.Item
                            name="item_id"
                            label="Позиция"
                            rules={[{ required: true }]}
                        >
                            <Select
                                showSearch
                                optionFilterProp="label"
                                options={itemOptions}
                            />
                        </Form.Item>
                        <Form.Item
                            name="quantity"
                            label="Количество"
                            rules={[{ required: true }]}
                        >
                            <InputNumber
                                min={0.001}
                                step={1}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                        <Form.Item name="note" label="Примечание">
                            <Input.TextArea rows={2} />
                        </Form.Item>
                    </Form>
                </Modal>

                <Modal
                    title="Выдача сотруднику"
                    open={opModal === 'issue'}
                    width={760}
                    okText="Оформить выдачу"
                    okButtonProps={{
                        disabled: !issueLinesValid
                    }}
                    onCancel={() => {
                        setOpModal(null);
                        opForm.resetFields();
                        setIssueLines([]);
                        setIssueQr(null);
                    }}
                    onOk={() => opForm.submit()}
                    destroyOnClose
                >
                    <Form
                        form={opForm}
                        layout="vertical"
                        onFinish={runOp}
                    >
                        <Form.Item
                            name="user_id"
                            label="Сотрудник"
                            rules={[{ required: true, message: 'Выберите сотрудника' }]}
                        >
                            <Select
                                showSearch
                                optionFilterProp="label"
                                placeholder="Кому выдаём"
                                options={installers.map(u => ({
                                    value: u.id,
                                    label: u.name
                                }))}
                            />
                        </Form.Item>

                        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                            Наберите позиции, затем оформите выдачу. Товар спишется со склада и будет ждать подтверждения сотрудника.
                        </Text>

                        <Space
                            wrap
                            style={{ width: '100%', marginBottom: 12 }}
                            align="start"
                        >
                            <Select
                                showSearch
                                optionFilterProp="label"
                                placeholder="Позиция ТМЦ"
                                style={{ minWidth: 360 }}
                                value={pickItemId}
                                onChange={id => {
                                    setPickItemId(id);
                                    setPickQty(1);
                                }}
                                options={issueItemOptions}
                            />
                            <InputNumber
                                min={0.001}
                                max={
                                    pickedStockLeft > 0
                                        ? pickedStockLeft
                                        : 0.001
                                }
                                step={1}
                                value={pickQty}
                                disabled={
                                    !pickedIssueItem ||
                                    pickedStockLeft <= 0
                                }
                                onChange={setPickQty}
                                placeholder="Кол-во"
                            />
                            <Button
                                icon={<PlusOutlined />}
                                disabled={
                                    !pickedIssueItem ||
                                    pickedStockLeft <= 0 ||
                                    !(Number(pickQty) > 0) ||
                                    Number(pickQty) > pickedStockLeft
                                }
                                onClick={addIssueLine}
                            >
                                Добавить
                            </Button>
                            {pickedIssueItem ? (
                                <Text type="secondary">
                                    {pickedStockLeft > 0
                                        ? `Доступно к выдаче: ${pickedStockLeft} ${pickedIssueItem.unit}`
                                        : 'Нет доступного остатка'}
                                </Text>
                            ) : null}
                        </Space>

                        <Table
                            size="small"
                            rowKey="item_id"
                            pagination={false}
                            locale={{ emptyText: 'Список выдачи пуст' }}
                            dataSource={issueLines}
                            columns={[
                                {
                                    title: 'Позиция',
                                    render: (_, row) => (
                                        <div>
                                            <div>{row.name}</div>
                                            {row.sku ? (
                                                <Text type="secondary">
                                                    {row.sku}
                                                </Text>
                                            ) : null}
                                        </div>
                                    )
                                },
                                {
                                    title: 'Кол-во',
                                    width: 160,
                                    render: (_, row) => (
                                        <InputNumber
                                            min={0.001}
                                            max={row.max}
                                            value={row.quantity}
                                            onChange={value =>
                                                setIssueLineQty(
                                                    row.item_id,
                                                    value
                                                )
                                            }
                                        />
                                    )
                                },
                                {
                                    title: 'Ед.',
                                    dataIndex: 'unit',
                                    width: 70
                                },
                                {
                                    title: '',
                                    width: 50,
                                    render: (_, row) => (
                                        <Button
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() =>
                                                setIssueLines(list =>
                                                    list.filter(
                                                        line =>
                                                            line.item_id !==
                                                            row.item_id
                                                    )
                                                )
                                            }
                                        />
                                    )
                                }
                            ]}
                        />

                        <Form.Item
                            name="note"
                            label="Примечание"
                            style={{ marginTop: 12 }}
                        >
                            <Input.TextArea rows={2} />
                        </Form.Item>

                        <Space
                            direction="vertical"
                            size={8}
                            style={{ width: '100%' }}
                        >
                            <Space wrap>
                                <Button
                                    icon={<QrcodeOutlined />}
                                    loading={qrLoading}
                                    disabled={
                                        !issueLinesValid ||
                                        !issueUserId
                                    }
                                    onClick={() => generateIssueQr()}
                                >
                                    Сгенерировать QR
                                </Button>
                                {issueQr?.code ? (
                                    <Text>
                                        Код:{' '}
                                        <Text code copyable>
                                            {issueQr.code}
                                        </Text>
                                    </Text>
                                ) : !issueLines.length ? (
                                    <Text type="secondary">
                                        Сначала добавьте позиции — затем
                                        можно сгенерировать QR
                                    </Text>
                                ) : (
                                    <Text type="secondary">
                                        Если не нажать, QR с позициями
                                        создастся автоматически при выдаче
                                    </Text>
                                )}
                            </Space>
                            {issueQr?.qr_data_url ? (
                                <div>
                                    <img
                                        src={issueQr.qr_data_url}
                                        alt="QR выдачи"
                                        style={{
                                            width: 160,
                                            height: 160
                                        }}
                                    />
                                    <div style={{ marginTop: 8 }}>
                                        {issueLines.map(line => (
                                            <div key={line.item_id}>
                                                <Text>
                                                    {line.name} —{' '}
                                                    <Text strong>
                                                        {line.quantity}{' '}
                                                        {line.unit}
                                                    </Text>
                                                </Text>
                                            </div>
                                        ))}
                                    </div>
                                    <Text type="secondary">
                                        При добавлении позиции QR
                                        пересобирается сам
                                    </Text>
                                </div>
                            ) : null}
                        </Space>
                    </Form>
                </Modal>

                <Modal
                    title="Выдача оформлена"
                    open={Boolean(issueResult)}
                    onCancel={() => setIssueResult(null)}
                    footer={
                        <Button
                            type="primary"
                            onClick={() => setIssueResult(null)}
                        >
                            Закрыть
                        </Button>
                    }
                >
                    {issueResult ? (
                        <Space
                            direction="vertical"
                            size={12}
                            style={{ width: '100%', textAlign: 'center' }}
                        >
                            <Text>
                                Сотрудник:{' '}
                                <Text strong>
                                    {userLabel(issueResult.user)}
                                </Text>
                            </Text>
                            <Text>
                                Код выдачи:{' '}
                                <Text code copyable>
                                    {issueResult.code}
                                </Text>
                            </Text>
                            {issueResult.qr_data_url ? (
                                <img
                                    src={issueResult.qr_data_url}
                                    alt="QR выдачи"
                                    style={{
                                        width: 220,
                                        height: 220,
                                        margin: '0 auto'
                                    }}
                                />
                            ) : (
                                <Tag icon={<QrcodeOutlined />}>
                                    {issueResult.qr_payload}
                                </Tag>
                            )}
                            <Text type="secondary">
                                QR можно показать сотруднику. Подтверждение в
                                личном кабинете подключим отдельно — сейчас
                                выдача ждёт приёмки.
                            </Text>
                            <Table
                                size="small"
                                pagination={false}
                                rowKey="holding_id"
                                dataSource={issueResult.lines || []}
                                columns={[
                                    {
                                        title: 'Позиция',
                                        dataIndex: 'name'
                                    },
                                    {
                                        title: 'Кол-во',
                                        render: (_, row) =>
                                            `${row.quantity} ${row.unit}`
                                    }
                                ]}
                            />
                        </Space>
                    ) : null}
                </Modal>
            </div>
        </Layout>
    );
};

export default TmcWarehousePage;
